import React, { useState } from 'react';
import { Check, Shield, Zap, RotateCcw, Target } from 'lucide-react';
import './PricingPage.css';
import { PLAN_LIMITS } from '../constants/planLimits';
import { useAuth } from '../AuthContext';
import { auth } from '../firebase'; // ✅ Import Firebase auth directly

const PLAN_META = {
  free: {
    tagline: 'Try AI-powered presentation creation',
    support: 'Community support',
  },
  starter: {
    tagline: 'For individual creators',
    support: 'Email support',
  },
  creator: {
    tagline: 'For educators and content professionals',
    support: 'Priority support',
    highlight: true,
  },
  professional: {
    tagline: 'For teams and course creators',
    support: 'Dedicated support',
  },
};

const PLAN_ORDER = ['free', 'starter', 'creator', 'professional'];

const FAQS = [
  { q: 'How does billing work?', a: "You're billed monthly on the date you subscribe. All plans renew automatically, but you can cancel anytime before your next billing date." },
  { q: 'What happens when I reach my monthly limit?', a: "You'll be notified when approaching your limit. Your existing projects remain accessible — you just can't create new ones until you upgrade or your limit resets next month." },
  { q: 'Can I change plans later?', a: 'Yes. Upgrade anytime with immediate effect. Downgrades take effect at your next billing cycle, and you keep your current features until then.' },
  { q: 'What export formats are supported?', a: 'All plans include unlimited PNG and PPTX exports.' },
  { q: 'Do you offer refunds?', a: 'Yes. We offer a 30-day money-back guarantee on all plans. Contact Support@slideprintz.com within 30 days of purchase for a full refund.' },
  { q: 'Are there any additional fees?', a: 'No. The price you see is the price you pay. No setup fees, no transaction fees, no hidden costs.' },
];

const PricingPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { user, showAuthModal } = useAuth();

  const handleUpgrade = async (planKey) => {
    const plan = PLAN_LIMITS[planKey];

    // Check if product ID exists
    if (!plan.productId) {
      alert('This plan is not available for purchase yet. Please check back soon!');
      return;
    }

    // Check if user is authenticated - show auth modal instead of alert
    if (!user) {
      showAuthModal('signup');
      return;
    }

    setIsLoading(true);

    try {
      // ✅ Get ID token directly from Firebase auth.currentUser
      const idToken = await auth.currentUser.getIdToken();

      // Call your backend to create checkout session
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL || 'http://localhost:5000'}/api/create-checkout`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`
          },
          body: JSON.stringify({
            productId: plan.productId,
            planType: planKey
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create checkout session');
      }

      const data = await response.json();

      // Redirect to Dodo checkout page
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }

    } catch (error) {

      alert(error.message || 'Failed to create checkout session. Please try again.');
      setIsLoading(false);
    }
  };

  // Handle Free Plan - Show auth modal if not logged in
  const handleGetStartedFree = () => {
    if (!user) {
      showAuthModal('signup');
    } else {
      window.location.href = '/generate';
    }
  };

  const handleCtaClick = (planKey) => {
    if (planKey === 'free') {
      handleGetStartedFree();
    } else {
      handleUpgrade(planKey);
    }
  };

  const renderPlanCard = (planKey) => {
    const plan = PLAN_LIMITS[planKey];
    const meta = PLAN_META[planKey];
    const isFree = planKey === 'free';
    const price = plan.price ? plan.price.toFixed(0) : '0';
    const ctaLabel = !isFree && isLoading ? 'Processing...' : isFree ? 'Start Free' : `Get ${plan.name}`;

    return (
      <div key={planKey} className={`pricing-card ${meta.highlight ? 'pricing-card-highlight' : ''}`}>
        {meta.highlight && <div className="popular-stamp">Most printed</div>}
        <h3 className="plan-name">{plan.name}</h3>
        <p className="plan-tagline">{meta.tagline}</p>
        <div className="plan-price-row">
          <span className="plan-price">${price}</span>
          {!isFree && <span className="plan-price-interval">/month</span>}
        </div>
        <ul className="plan-features">
          <li className="plan-feature-item">
            <span className="plan-check"><Check size={12} color="#FFFFFF" strokeWidth={3} /></span>
            <span>{plan.monthlyProjects.prompt} Prompt-Based Projects per Month</span>
          </li>
          {plan.features.map((feature, i) => (
            <li key={i} className="plan-feature-item">
              <span className="plan-check"><Check size={12} color="#FFFFFF" strokeWidth={3} /></span>
              <span>{feature}</span>
            </li>
          ))}
        </ul>
        <p className="plan-support">Support: {meta.support}</p>
        <button
          className="plan-cta"
          disabled={!isFree && isLoading}
          onClick={() => handleCtaClick(planKey)}
        >
          {ctaLabel}
        </button>
      </div>
    );
  };

  return (
    <div className="pricing-page">

      {/* PRICING */}
      <section className="pricing-section">
        <span className="eyebrow eyebrow-center">PRICING</span>
        <h1 className="section-title">Simple, transparent pricing</h1>
        <p className="section-subtitle">Pay only for what you need. Choose the plan that fits.</p>
        <div className="pricing-grid-4">
          {PLAN_ORDER.map((planKey) => renderPlanCard(planKey))}
        </div>
      </section>

      {/* TRUST */}
      <section className="trust-section">
        <div className="trust-grid">
          <div className="trust-item">
            <div className="trust-icon"><Shield size={20} strokeWidth={2} /></div>
            <h4>Secure Payments</h4>
            <p>All transactions protected by 256-bit SSL encryption</p>
          </div>
          <div className="trust-item">
            <div className="trust-icon"><Zap size={20} strokeWidth={2} /></div>
            <h4>Instant Activation</h4>
            <p>Your plan upgrades immediately after payment</p>
          </div>
          <div className="trust-item">
            <div className="trust-icon"><RotateCcw size={20} strokeWidth={2} /></div>
            <h4>Cancel Anytime</h4>
            <p>No long-term contracts or hidden fees</p>
          </div>
          <div className="trust-item">
            <div className="trust-icon"><Target size={20} strokeWidth={2} /></div>
            <h4>30-Day Money Back</h4>
            <p>Full refund if you're not satisfied</p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="faq-section">
        <h2 className="section-title">Frequently asked questions</h2>
        <p className="section-subtitle">Everything you need to know about billing</p>
        <div className="faq-container">
          {FAQS.map((item, i) => (
            <div className="faq-card" key={i}>
              <span className="faq-index">N&deg;{String(i + 1).padStart(2, '0')}</span>
              <h3 className="faq-title">{item.q}</h3>
              <p className="faq-desc">{item.a}</p>
            </div>
          ))}
        </div>
      </section>

    </div>
  );
};

export default PricingPage;